import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "yup_admin_token";

const RANGES = [
  { label: "24h", days: 1 },
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "All", days: 0 },
];

const SCROLL_MILESTONES = [25, 50, 75, 100];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Card = ({ title, children, action }) => (
  <section
    style={{
      background: "var(--yup-white, #fff)",
      border: "1px solid var(--yup-border, #e0d8cc)",
      padding: "24px 28px",
      marginBottom: "20px",
    }}
  >
    <header
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "16px",
      }}
    >
      <h3
        style={{
          fontSize: "11px",
          letterSpacing: "2px",
          textTransform: "uppercase",
          color: "var(--yup-warm, #c4622d)",
          margin: 0,
        }}
      >
        {title}
      </h3>
      {action}
    </header>
    {children}
  </section>
);

const Stat = ({ label, value }) => (
  <div
    style={{
      background: "var(--yup-surface, #f2ece2)",
      padding: "20px 24px",
      borderLeft: "2px solid var(--yup-accent, #2c4a3e)",
    }}
  >
    <div
      style={{
        fontSize: "10px",
        letterSpacing: "2px",
        textTransform: "uppercase",
        color: "var(--yup-muted, #7a7068)",
        marginBottom: "6px",
      }}
    >
      {label}
    </div>
    <div
      className="font-serif"
      style={{
        fontSize: "32px",
        fontWeight: 300,
        color: "var(--yup-ink, #1c1814)",
        lineHeight: 1,
      }}
    >
      {value}
    </div>
  </div>
);

const BarRow = ({ label, count, max }) => {
  const pct = max > 0 ? Math.max(2, Math.round((count / max) * 100)) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
      <div
        style={{
          flex: "0 0 200px",
          fontSize: "13px",
          color: "var(--yup-ink, #1c1814)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={label}
      >
        {label}
      </div>
      <div
        style={{
          flex: 1,
          height: "14px",
          background: "var(--yup-surface, #f2ece2)",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            width: `${pct}%`,
            background: "var(--yup-accent, #2c4a3e)",
          }}
        />
      </div>
      <div
        style={{
          flex: "0 0 60px",
          textAlign: "right",
          fontSize: "13px",
          fontVariantNumeric: "tabular-nums",
          color: "var(--yup-muted, #7a7068)",
        }}
      >
        {count.toLocaleString()}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [token, setToken] = useState(() => {
    try {
      return window.localStorage.getItem(TOKEN_KEY) || "";
    } catch {
      return "";
    }
  });
  const [pwInput, setPwInput] = useState("");
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (t, d) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/analytics/summary?days=${d}`, {
        headers: { "X-Admin-Token": t },
      });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        setError("Incorrect password.");
        setToken("");
        try {
          window.localStorage.removeItem(TOKEN_KEY);
        } catch {}
      } else {
        setError("Failed to load — try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token, days);
  }, [token, days, load]);

  const handleLogin = (e) => {
    e.preventDefault();
    const t = pwInput.trim();
    if (!t) return;
    try {
      window.localStorage.setItem(TOKEN_KEY, t);
    } catch {}
    setToken(t);
  };

  const handleLogout = () => {
    try {
      window.localStorage.removeItem(TOKEN_KEY);
    } catch {}
    setToken("");
    setData(null);
  };

  // Group scroll-depth by page for the funnel widget
  const scrollByPage = useMemo(() => {
    if (!data?.scroll_depth) return [];
    const sessionsByPage = {};
    data.scroll_depth.forEach((row) => {
      if (!sessionsByPage[row.page]) sessionsByPage[row.page] = {};
      sessionsByPage[row.page][row.milestone] = row.sessions;
    });
    return Object.entries(sessionsByPage).map(([page, milestones]) => ({
      page,
      values: SCROLL_MILESTONES.map((m) => milestones[m] || 0),
    }));
  }, [data]);

  // ── Login screen ──────────────────────────────────────────────────────
  if (!token) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--yup-bg, #f9f5ef)",
          padding: "24px",
        }}
      >
        <form
          onSubmit={handleLogin}
          data-testid="dashboard-login-form"
          style={{
            background: "var(--yup-white, #fff)",
            border: "1px solid var(--yup-border, #e0d8cc)",
            padding: "40px 36px",
            maxWidth: "380px",
            width: "100%",
          }}
        >
          <div
            className="font-serif"
            style={{
              fontSize: "22px",
              fontWeight: 600,
              letterSpacing: "3px",
              color: "var(--yup-accent, #2c4a3e)",
              textTransform: "uppercase",
              marginBottom: "8px",
            }}
          >
            YUP
          </div>
          <h1
            className="font-serif"
            style={{
              fontSize: "28px",
              fontWeight: 300,
              color: "var(--yup-ink, #1c1814)",
              marginBottom: "8px",
            }}
          >
            Dashboard
          </h1>
          <p
            style={{
              fontSize: "13px",
              color: "var(--yup-muted, #7a7068)",
              marginBottom: "24px",
            }}
          >
            Enter the admin password to continue.
          </p>
          <input
            type="password"
            value={pwInput}
            onChange={(e) => setPwInput(e.target.value)}
            placeholder="Password"
            autoFocus
            data-testid="dashboard-password-input"
            style={{
              width: "100%",
              padding: "14px 16px",
              background: "var(--yup-surface, #f2ece2)",
              border: "1px solid var(--yup-border, #e0d8cc)",
              fontFamily: "inherit",
              fontSize: "15px",
              outline: "none",
              marginBottom: "12px",
            }}
          />
          {error && (
            <p
              style={{
                fontSize: "13px",
                color: "var(--yup-warm, #c4622d)",
                marginBottom: "12px",
              }}
              data-testid="dashboard-login-error"
            >
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="dashboard-login-button"
            className="btn-yup-primary"
            style={{ width: "100%", padding: "14px" }}
          >
            Sign in
          </button>
        </form>
      </div>
    );
  }

  // ── Dashboard ─────────────────────────────────────────────────────────
  const pvMax = Math.max(...(data?.page_views?.map((r) => r.count) || [0]));
  const pillarMax = Math.max(...(data?.pillar_opens?.map((r) => r.count) || [0]));
  const obMax = Math.max(...(data?.outbound_clicks?.map((r) => r.count) || [0]));
  const refMax = Math.max(...(data?.referrers?.map((r) => r.count) || [0]));

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--yup-bg, #f9f5ef)",
        padding: "32px 24px 64px",
      }}
      data-testid="dashboard-root"
    >
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "28px",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div>
            <div
              className="font-serif"
              style={{
                fontSize: "18px",
                fontWeight: 600,
                letterSpacing: "3px",
                color: "var(--yup-accent, #2c4a3e)",
                textTransform: "uppercase",
              }}
            >
              YUP
            </div>
            <h1
              className="font-serif"
              style={{ fontSize: "32px", fontWeight: 300, color: "var(--yup-ink, #1c1814)" }}
            >
              Dashboard
            </h1>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ display: "flex", gap: "2px" }} data-testid="dashboard-range-tabs">
              {RANGES.map((r) => (
                <button
                  key={r.label}
                  type="button"
                  onClick={() => setDays(r.days)}
                  data-testid={`dashboard-range-${r.label}`}
                  style={{
                    padding: "8px 14px",
                    fontSize: "12px",
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    border: "1px solid var(--yup-border, #e0d8cc)",
                    background:
                      days === r.days ? "var(--yup-accent, #2c4a3e)" : "var(--yup-white, #fff)",
                    color: days === r.days ? "var(--yup-bg, #f9f5ef)" : "var(--yup-ink, #1c1814)",
                    cursor: "pointer",
                  }}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="dashboard-logout"
              style={{
                fontSize: "12px",
                letterSpacing: "1px",
                textTransform: "uppercase",
                color: "var(--yup-muted, #7a7068)",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                borderBottom: "1px solid var(--yup-border, #e0d8cc)",
                paddingBottom: "2px",
              }}
            >
              Sign out
            </button>
          </div>
        </header>

        {loading && (
          <p style={{ color: "var(--yup-muted, #7a7068)" }} data-testid="dashboard-loading">
            Loading…
          </p>
        )}
        {error && (
          <p style={{ color: "var(--yup-warm, #c4622d)" }} data-testid="dashboard-error">
            {error}
          </p>
        )}

        {data && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "14px",
                marginBottom: "24px",
              }}
              data-testid="dashboard-stats"
            >
              <Stat label="Unique visitors" value={data.unique_sessions.toLocaleString()} />
              <Stat
                label="Page views"
                value={(data.page_views.reduce((s, r) => s + r.count, 0)).toLocaleString()}
              />
              <Stat label="Registrations" value={data.registrations.count.toLocaleString()} />
              <Stat label="Supporters" value={data.supporters.count.toLocaleString()} />
            </div>

            <Card title="Page views">
              {data.page_views.length === 0 ? (
                <p style={{ fontSize: "13px", color: "var(--yup-muted, #7a7068)" }}>
                  No page views yet.
                </p>
              ) : (
                data.page_views.map((row) => (
                  <BarRow key={row._id} label={row._id} count={row.count} max={pvMax} />
                ))
              )}
            </Card>

            <Card title="Scroll depth — where people stop">
              {scrollByPage.length === 0 ? (
                <p style={{ fontSize: "13px", color: "var(--yup-muted, #7a7068)" }}>
                  No scroll data yet.
                </p>
              ) : (
                scrollByPage.map((row) => {
                  const top = Math.max(...row.values, 1);
                  return (
                    <div key={row.page} style={{ marginBottom: "20px" }}>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "var(--yup-ink, #1c1814)",
                          marginBottom: "6px",
                        }}
                      >
                        {row.page}
                      </div>
                      <div style={{ display: "flex", gap: "6px" }}>
                        {SCROLL_MILESTONES.map((m, i) => {
                          const v = row.values[i];
                          const pct = Math.round((v / top) * 100);
                          return (
                            <div key={m} style={{ flex: 1, textAlign: "center" }}>
                              <div
                                style={{
                                  fontSize: "10px",
                                  letterSpacing: "1px",
                                  textTransform: "uppercase",
                                  color: "var(--yup-muted, #7a7068)",
                                  marginBottom: "4px",
                                }}
                              >
                                {m}%
                              </div>
                              <div
                                style={{
                                  height: "70px",
                                  background: "var(--yup-surface, #f2ece2)",
                                  position: "relative",
                                  display: "flex",
                                  alignItems: "flex-end",
                                }}
                              >
                                <div
                                  style={{
                                    width: "100%",
                                    height: `${pct}%`,
                                    background: "var(--yup-accent, #2c4a3e)",
                                    transition: "height 0.3s",
                                  }}
                                />
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "var(--yup-ink, #1c1814)",
                                  marginTop: "4px",
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                {v}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </Card>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                gap: "20px",
              }}
            >
              <Card title="Pillars — most opened">
                {data.pillar_opens.length === 0 ? (
                  <p style={{ fontSize: "13px", color: "var(--yup-muted, #7a7068)" }}>
                    No pillar opens yet.
                  </p>
                ) : (
                  data.pillar_opens.map((row) => (
                    <BarRow key={row._id} label={row._id} count={row.count} max={pillarMax} />
                  ))
                )}
              </Card>

              <Card title="Outbound clicks">
                {data.outbound_clicks.length === 0 ? (
                  <p style={{ fontSize: "13px", color: "var(--yup-muted, #7a7068)" }}>
                    No outbound clicks yet.
                  </p>
                ) : (
                  data.outbound_clicks.map((row) => (
                    <BarRow key={row._id} label={row._id} count={row.count} max={obMax} />
                  ))
                )}
              </Card>
            </div>

            <Card title="Top referrers">
              {data.referrers.length === 0 ? (
                <p style={{ fontSize: "13px", color: "var(--yup-muted, #7a7068)" }}>
                  No referrer data yet.
                </p>
              ) : (
                data.referrers.map((row) => (
                  <BarRow key={row._id} label={row._id} count={row.count} max={refMax} />
                ))
              )}
            </Card>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                gap: "20px",
              }}
            >
              <Card title="Recent registrations">
                {data.registrations.recent.length === 0 ? (
                  <p style={{ fontSize: "13px", color: "var(--yup-muted, #7a7068)" }}>
                    No registrations yet.
                  </p>
                ) : (
                  <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {data.registrations.recent.map((r, i) => (
                      <li
                        key={i}
                        style={{
                          padding: "10px 0",
                          borderBottom: "1px solid var(--yup-border, #e0d8cc)",
                          fontSize: "13px",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
                          <strong style={{ color: "var(--yup-ink, #1c1814)", fontWeight: 500 }}>
                            {r.firstName}
                          </strong>
                          <span style={{ color: "var(--yup-muted, #7a7068)" }}>
                            {formatDate(r.createdAt)}
                          </span>
                        </div>
                        <div style={{ color: "var(--yup-muted, #7a7068)" }}>{r.email}</div>
                        {r.reason && (
                          <div
                            style={{
                              color: "var(--yup-muted, #7a7068)",
                              fontStyle: "italic",
                              marginTop: "4px",
                            }}
                          >
                            "{r.reason}"
                          </div>
                        )}
                        {!r.mailchimpSynced && (
                          <div
                            style={{
                              color: "var(--yup-warm, #c4622d)",
                              fontSize: "11px",
                              marginTop: "4px",
                            }}
                          >
                            ⚠ not synced to Mailchimp
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <Card title="Recent supporters">
                {data.supporters.recent.length === 0 ? (
                  <p style={{ fontSize: "13px", color: "var(--yup-muted, #7a7068)" }}>
                    No supporter submissions yet.
                  </p>
                ) : (
                  <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {data.supporters.recent.map((r, i) => (
                      <li
                        key={i}
                        style={{
                          padding: "10px 0",
                          borderBottom: "1px solid var(--yup-border, #e0d8cc)",
                          fontSize: "13px",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
                          <strong style={{ color: "var(--yup-ink, #1c1814)", fontWeight: 500 }}>
                            {r.firstName} {r.role ? `· ${r.role}` : ""}
                          </strong>
                          <span style={{ color: "var(--yup-muted, #7a7068)" }}>
                            {formatDate(r.createdAt)}
                          </span>
                        </div>
                        <div style={{ color: "var(--yup-muted, #7a7068)" }}>{r.email}</div>
                        {r.message && (
                          <div
                            style={{
                              color: "var(--yup-muted, #7a7068)",
                              fontStyle: "italic",
                              marginTop: "4px",
                            }}
                          >
                            "{r.message}"
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
